import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../../lib/api";
import { Table, TableBody, TableCell, TableRow } from "../../../../../components/ui/table";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "../shared";
import { ACCOUNTING_BASE } from "../accountingNav";
import { ReportFilterBar, ReportPrintShell, BalanceCheckBanner, useFinancialYears, downloadReportCsv, formatCurrency } from "./reportShared";

// Phase 17 item 4's explicit category list ("ASSETS - Bank, Cash, Fixed
// Assets, Fixed Deposits, Member Receivables, Other Assets"; "LIABILITIES -
// Vendor Payables, Member Advances, Other Liabilities" - Funds/Reserves is
// already its own separate "Funds & Capital" section below, unchanged).
// Purely a DISPLAY grouping over the exact same rows/totals/balance-check
// the engine already computes - the underlying ledgerGroup taxonomy has no
// separate "Fixed Asset" vs "Fixed Deposit" distinction (a pre-existing,
// documented limitation from earlier phases), so both fall under one
// "Fixed Assets & Other Assets" bucket rather than inventing a new
// classification here.
const ASSET_CATEGORY_LABELS = { bank: "Bank", cash: "Cash", member: "Member Receivables" };
const ASSET_CATEGORY_ORDER = ["bank", "cash", "member", "other"];
const LIABILITY_CATEGORY_LABELS = { vendor: "Vendor Payables", member: "Member Advances" };
const LIABILITY_CATEGORY_ORDER = ["vendor", "member", "other"];

function categorize(rows, labels, order, fallbackLabel) {
  const buckets = {};
  for (const r of rows) {
    const key = labels[r.ledgerGroup] ? r.ledgerGroup : "other";
    (buckets[key] = buckets[key] || []).push(r);
  }
  return order.filter((k) => buckets[k]?.length).map((k) => ({ key: k, label: labels[k] || fallbackLabel, rows: buckets[k] }));
}

/**
 * Phase 8 - Balance Sheet (items 24/25, 32). Assets = Liabilities + Funds &
 * Capital (current-period Surplus/Deficit folded into Funds & Capital) as
 * of a single date. The Difference is always shown, never hidden (item 25).
 * Fixed Deposits appear automatically wherever their own ledger sits
 * (item 32) - no separate handling needed.
 */
export default function BalanceSheetPage() {
  const navigate = useNavigate();
  const financialYears = useFinancialYears();
  const [financialYearId, setFinancialYearId] = useState("");
  const [asOfDate, setAsOfDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = { financialYearId, asOfDate };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reports/balance-sheet", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load balance sheet"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [financialYearId, asOfDate]);

  const openLedger = (ledgerId) => ledgerId && navigate(`${ACCOUNTING_BASE}/reports/general-ledger?ledgerId=${ledgerId}&toDate=${asOfDate || data?.asOfDate || ""}`);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Balance Sheet</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Assets, Liabilities and Funds &amp; Capital as of a chosen date.</p>
      </div>

      <div className="report-screen-only space-y-5">
        <ReportFilterBar
          financialYears={financialYears} financialYearId={financialYearId} onFinancialYearChange={setFinancialYearId}
          toDate={asOfDate} onToDateChange={setAsOfDate}
          onExport={() => downloadReportCsv("/society/accounting/reports/balance-sheet", params, `balance-sheet-${data?.asOfDate || ""}.csv`)}
          onPrint={() => window.print()}
        />

        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : !data || (data.assets.length === 0 && data.liabilities.length === 0 && data.fundsAndCapital.length === 0) ? (
          <EmptyState>No posted accounting activity as of this date yet.</EmptyState>
        ) : (
          <>
            <div className="grid gap-5 lg:grid-cols-2">
              <SectionCard title="Assets" subtitle={`As of ${data.asOfDate}`}>
                <BSTable rows={data.assets} total={data.totalAssets} onOpen={openLedger} categories={categorize(data.assets, ASSET_CATEGORY_LABELS, ASSET_CATEGORY_ORDER, "Fixed Assets & Other Assets")} />
              </SectionCard>
              <div className="space-y-5">
                <SectionCard title="Liabilities">
                  <BSTable rows={data.liabilities} total={data.totalLiabilities} onOpen={openLedger} categories={categorize(data.liabilities, LIABILITY_CATEGORY_LABELS, LIABILITY_CATEGORY_ORDER, "Other Liabilities")} />
                </SectionCard>
                <SectionCard title="Funds & Capital">
                  <BSTable rows={data.fundsAndCapital} total={data.totalFundsAndCapital} onOpen={openLedger} />
                </SectionCard>
              </div>
            </div>
            <BalanceCheckBanner label="Balance Sheet Difference" difference={data.difference} okLabel="₹0.00 - Assets = Liabilities + Funds & Capital" />
          </>
        )}
      </div>

      {data && (
        <div className="report-print-only">
          <ReportPrintShell reportName="Balance Sheet" rangeLabel={`As of ${data.asOfDate}${data.financialYear ? ` — FY ${data.financialYear}` : ""}`}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-1 font-bold">Assets</div>
                <PrintBSTable rows={data.assets} total={data.totalAssets} />
              </div>
              <div>
                <div className="mb-1 font-bold">Liabilities</div>
                <PrintBSTable rows={data.liabilities} total={data.totalLiabilities} />
                <div className="mb-1 mt-3 font-bold">Funds &amp; Capital</div>
                <PrintBSTable rows={data.fundsAndCapital} total={data.totalFundsAndCapital} />
              </div>
            </div>
            <div className="mt-3 font-bold">Difference: {formatCurrency(Math.abs(data.difference))} {data.balanced ? "(Balanced)" : "(MISMATCH)"}</div>
          </ReportPrintShell>
        </div>
      )}
    </div>
  );
}

function BSTable({ rows, total, onOpen, categories }) {
  // When every row falls into "other" (categories collapses to nothing
  // useful) or there's nothing to show, fall back to the original flat
  // list - sub-headers only earn their place when they actually split the
  // data into more than one meaningful group.
  const showCategories = categories && categories.length > 1;
  return (
    <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
      <Table>
        <TableBody>
          {showCategories ? categories.map((cat) => (
            <Fragment key={cat.key}>
              <TableRow className="bg-[#F9FAFB]"><TableCell colSpan={2} className="text-xs font-bold uppercase tracking-wide text-[#6B7280]">{cat.label}</TableCell></TableRow>
              {cat.rows.map((r, i) => (
                <TableRow key={r.ledgerId || i}>
                  <TableCell>{r.ledgerId ? <button type="button" className="text-left font-semibold text-[#111827] hover:underline" onClick={() => onOpen(r.ledgerId)}>{r.ledgerName}</button> : r.ledgerName}</TableCell>
                  <TableCell className="text-right">{formatCurrency(r.balance)}</TableCell>
                </TableRow>
              ))}
            </Fragment>
          )) : rows.map((r, i) => (
            <TableRow key={r.ledgerId || i}>
              <TableCell>{r.ledgerId ? <button type="button" className="text-left font-semibold text-[#111827] hover:underline" onClick={() => onOpen(r.ledgerId)}>{r.ledgerName}</button> : r.ledgerName}</TableCell>
              <TableCell className="text-right">{formatCurrency(r.balance)}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-sm text-[#6B7280]">None</TableCell></TableRow>}
          <TableRow className="border-t-2 border-[#111827] font-bold"><TableCell>Total</TableCell><TableCell className="text-right">{formatCurrency(total)}</TableCell></TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function PrintBSTable({ rows, total }) {
  return (
    <table className="w-full border-collapse text-[10px]">
      <tbody>
        {rows.map((r, i) => <tr key={r.ledgerId || i} className="border-b border-gray-300"><td className="py-0.5">{r.ledgerName}</td><td className="py-0.5 text-right">{formatCurrency(r.balance)}</td></tr>)}
        <tr className="border-t border-black font-bold"><td className="py-1">Total</td><td className="py-1 text-right">{formatCurrency(total)}</td></tr>
      </tbody>
    </table>
  );
}
